import {
  listDrafts,
  removeDraft,
  typeLabel,
  initCartUi,
  CART_EVENT,
} from '../../../js/bookingDraft.js';
import { getTurnstileConfig, mountTurnstile } from '../../../js/turnstile.js';
import {
  isLeadContactValid,
  isSubmissionId,
  leadSignature,
  postReservationLead,
  reuseSubmissionId,
  shouldPostLead,
  trainingTypeForItems,
} from '../../../js/reservationLead.js';

const i18n = (key, fallback) => {
  const bag = typeof window !== 'undefined' ? window.hskiI18n : null;
  const value = bag && typeof bag[key] === 'string' ? bag[key] : '';
  return value || fallback;
};

const i18nFormat = (key, fallback, ...args) => {
  let index = 0;
  return i18n(key, fallback).replace(/%[ds]/g, () => String(args[index++] ?? ''));
};

const uiLocale = () => {
  const htmlLang = typeof document !== 'undefined' ? (document.documentElement.lang || '') : '';
  if (htmlLang.toLowerCase().startsWith('en')) return htmlLang;
  if (window.hskiBooking?.lang === 'en') return 'en-GB';
  return 'pl-PL';
};

const ICON_BAG = '<svg width="36" height="36" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M7.5 6V6.75H5.513C4.553 6.75 3.749 7.474 3.648 8.429L2.385 20.429A2.25 2.25 0 0 0 4.25 22.5h15.5a2.25 2.25 0 0 0 1.865-2.071L20.352 8.429A2.25 2.25 0 0 0 18.487 6.75H16.5V6a4.5 4.5 0 0 0-9 0Zm4.5-3a3 3 0 0 0-3 3v.75h6V6a3 3 0 0 0-3-3Z" fill="currentColor"/></svg>';

let suppressCartRefresh = false;
let turnstileWidget = null;

const STEP_TOTAL = {
  travel: 3,
  course: 3,
  lesson: 3,
  instructor_lead: 2,
  academy_lead: 2,
  poles_lead: 2,
};

const CRM_CART_TYPES = new Set(['course', 'lesson']);

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const formatUpdated = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(uiLocale(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const countLabel = (count) => {
  const n = Number(count) || 0;
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (n === 1) return i18n('cart_count_one', '1 zapisana rezerwacja');
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
    return i18nFormat('cart_count_few', '%d zapisane rezerwacje', n);
  }
  return i18nFormat('cart_count_many', '%d zapisanych rezerwacji', n);
};

const prefersReducedMotion = () => (
  window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

const findCartItem = (root, id) => (
  root.querySelector(`.cart-page__item[data-draft-id="${CSS.escape(id)}"]`)
);

const newSubmissionId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `00000000-0000-4000-8000-${Date.now().toString(16).padStart(12, '0').slice(-12)}`;
};

const studentRows = (details = {}) => {
  const rows = Array.isArray(details.students) ? details.students : [];
  if (rows.length) return rows;
  if (details.student && typeof details.student === 'object') return [details.student];
  return [];
};

const contactComplete = (contact = {}) => Boolean(
  String(contact.first_name || '').trim()
  && String(contact.last_name || '').trim()
  && String(contact.email || '').trim()
  && String(contact.phone || '').trim(),
);

const itemReadyForCartSubmit = (item) => {
  if (!CRM_CART_TYPES.has(item.type)) return false;
  const details = item.payload?.details || {};
  const students = studentRows(details);
  if (!students.length) return false;
  return students.every((row) => (
    String(row.first_name || '').trim() && String(row.last_name || '').trim()
  ));
};

const collectReadyCartItems = () => listDrafts().filter(itemReadyForCartSubmit);

const pickSharedContact = (items) => {
  for (const item of items) {
    const contact = item.payload?.contact;
    if (contactComplete(contact)) return { ...contact };
  }
  return null;
};

const updateCartMeta = (root, count = listDrafts().length) => {
  const empty = root.querySelector('.js-cart-empty');
  const filled = root.querySelector('.js-cart-filled');
  const summaryCount = root.querySelector('.js-cart-summary-count');
  const lead = root.querySelector('.js-cart-lead');

  if (!count) {
    if (empty) {
      empty.hidden = false;
      if (!prefersReducedMotion()) {
        empty.classList.remove('is-entering');
        void empty.offsetWidth;
        empty.classList.add('is-entering');
      }
    }
    if (filled) filled.hidden = true;
    if (lead) lead.textContent = i18n('cart_lead', 'Niedokończone rezerwacje — wróć i dokończ, kiedy chcesz.');
    return;
  }

  if (empty) {
    empty.hidden = true;
    empty.classList.remove('is-entering');
  }
  if (filled) filled.hidden = false;
  if (summaryCount) summaryCount.textContent = countLabel(count);
  if (lead) lead.textContent = countLabel(count);
};

const syncCheckoutPanel = (root) => {
  const panel = root.querySelector('.js-cart-checkout');
  if (!panel) return;

  const ready = collectReadyCartItems();
  const lead = root.querySelector('.js-cart-checkout-lead');
  const fields = root.querySelector('.js-cart-checkout-fields');
  const hint = root.querySelector('.js-cart-checkout-hint');
  const submit = root.querySelector('.js-cart-checkout-submit');

  if (!ready.length) {
    panel.hidden = true;
    if (hint) {
      hint.hidden = true;
      hint.textContent = '';
    }
    return;
  }

  panel.hidden = false;
  const shared = pickSharedContact(ready);
  if (fields) fields.hidden = Boolean(shared);
  if (lead) {
    lead.textContent = shared
      ? i18nFormat(
        ready.length === 1 ? 'cart_send_one' : 'cart_send_many',
        ready.length === 1
          ? '%d pozycję wyślemy jednym zamówieniem (kursy i lekcje).'
          : '%d pozycje wyślemy jednym zamówieniem (kursy i lekcje).',
        ready.length,
      )
      : i18n('cart_checkout_need_contact', 'Uzupełnij dane zamawiającego, żeby wysłać kursy i lekcje razem.');
  }
  if (submit) {
    submit.disabled = false;
    submit.textContent = ready.length === 1
      ? i18n('cart_send_order', 'Wyślij zamówienie')
      : i18nFormat('cart_send_all', 'Wyślij wszystkie (%d)', ready.length);
  }

  if (shared && fields) {
    const map = {
      'cart-first-name': shared.first_name,
      'cart-last-name': shared.last_name,
      'cart-email': shared.email,
      'cart-phone': shared.phone,
    };
    Object.entries(map).forEach(([name, value]) => {
      const input = fields.querySelector(`[name="${name}"]`);
      if (input && !input.value) input.value = String(value || '');
    });
  }
};

const animateCartItemRemoval = (itemEl) => new Promise((resolve) => {
  if (!itemEl || prefersReducedMotion()) {
    resolve();
    return;
  }

  itemEl.classList.add('is-removing');
  itemEl.style.maxHeight = `${itemEl.offsetHeight}px`;

  requestAnimationFrame(() => {
    itemEl.style.maxHeight = '0px';
  });

  let settled = false;
  const finish = () => {
    if (settled) return;
    settled = true;
    itemEl.removeEventListener('transitionend', onTransitionEnd);
    window.clearTimeout(fallbackTimer);
    resolve();
  };

  const onTransitionEnd = (event) => {
    if (event.target !== itemEl || event.propertyName !== 'max-height') return;
    finish();
  };

  itemEl.addEventListener('transitionend', onTransitionEnd);
  const fallbackTimer = window.setTimeout(finish, 420);
});

const removeCartItem = async (root, id) => {
  const itemEl = findCartItem(root, id);
  if (itemEl) {
    await animateCartItemRemoval(itemEl);
    itemEl.remove();
  }
  suppressCartRefresh = true;
  removeDraft(id);
  updateCartMeta(root);
  syncCheckoutPanel(root);
  window.requestAnimationFrame(() => {
    suppressCartRefresh = false;
  });
};

const guestsLabel = (details = {}) => {
  const adults = Number(details.adults ?? details.participants ?? 0);
  const children = Number(details.children ?? 0);
  if (!adults && !children) return '';
  const parts = [];
  if (adults) parts.push(`${adults} ${adults === 1 ? i18n('adult_one', 'dorosły') : i18n('adult_many', 'dorosłych')}`);
  if (children) parts.push(`${children} ${children === 1 ? i18n('child_one', 'dziecko') : i18n('child_many', 'dzieci')}`);
  return parts.join(', ');
};

const itemTitle = (item) => {
  const details = item.payload?.details || {};
  if (item.type === 'travel') {
    const named = [details.resort, details.hotel || details.hotel_name].filter(Boolean).join(' — ');
    if (named) return named;
    return details.term_label || typeLabel(item.type);
  }
  if (item.type === 'lesson') {
    const kind = details.lesson_type === 'season'
      ? i18n('hour_package', 'Pakiet godzin')
      : typeLabel('lesson');
    return `${kind}${details.hours ? ` · ${details.hours} h` : ''}`;
  }
  if (item.type === 'course') {
    return details.course_label || typeLabel('course');
  }
  if (item.type === 'instructor_lead' || item.type === 'academy_lead' || item.type === 'poles_lead') {
    return details.course_type_label || details.course_label || typeLabel(item.type);
  }
  return typeLabel(item.type) || item.title || '';
};

const itemSubtitle = (item) => {
  const details = item.payload?.details || {};
  const when = details.term_label || details.date_from || details.dates || '';
  if (when) return String(when);
  const contact = item.payload?.contact || {};
  const name = [contact.first_name, contact.last_name].filter(Boolean).join(' ');
  const step = Math.max(1, Number(item.step) || 1);
  return [i18nFormat('step_n', 'Etap %d', step), name].filter(Boolean).join(' · ');
};

const resumeDraftUrl = (item) => {
  const base = item.resumeUrl || '#';
  if (base === '#') return base;
  try {
    const url = new URL(base, window.location.origin);
    const step = Math.max(1, Number(item.step) || 1);
    url.searchParams.set('step', String(step));
    if (item.id) url.searchParams.set('draft', item.id);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    const join = base.includes('?') ? '&' : '?';
    const step = Math.max(1, Number(item.step) || 1);
    const draft = item.id ? `&draft=${encodeURIComponent(item.id)}` : '';
    return `${base}${join}step=${step}${draft}`;
  }
};

const renderCart = (root) => {
  const list = root.querySelector('.js-cart-list');
  const empty = root.querySelector('.js-cart-empty');
  if (!list || !empty) return;

  const items = listDrafts();
  list.innerHTML = '';

  if (!items.length) {
    updateCartMeta(root, 0);
    syncCheckoutPanel(root);
    return;
  }

  updateCartMeta(root, items.length);
  syncCheckoutPanel(root);

  items.forEach((item) => {
    const details = item.payload?.details || {};
    const step = Math.max(1, Number(item.step) || 1);
    const total = STEP_TOTAL[item.type] || 3;
    const guests = guestsLabel(details);
    const updated = formatUpdated(item.updatedAt);
    const sub = itemSubtitle(item);
    const ready = itemReadyForCartSubmit(item);
    const tags = [
      typeLabel(item.type),
      i18nFormat('cart_step', 'Etap %d z %d', step, total),
      guests,
      ready ? i18n('cart_ready', 'Gotowe do wysyłki') : '',
    ].filter(Boolean);

    const li = document.createElement('li');
    li.className = 'cart-page__item';
    li.dataset.draftId = item.id;
    li.innerHTML = `
      <div class="cart-page__item-media" data-kind="${escapeHtml(item.type)}">
        ${ICON_BAG}
        <span class="cart-page__item-flag">${escapeHtml(typeLabel(item.type))}</span>
      </div>
      <div class="cart-page__item-body">
        <div>
          <div class="cart-page__item-tags">
            ${tags.map((tag) => `<span class="cart-page__item-tag">${escapeHtml(tag)}</span>`).join('')}
          </div>
          <h2 class="cart-page__item-title">${escapeHtml(itemTitle(item))}</h2>
          ${sub ? `<p class="cart-page__item-sub">${escapeHtml(sub)}</p>` : ''}
        </div>
        <div class="cart-page__item-foot">
          <p class="cart-page__item-meta">${updated ? escapeHtml(i18nFormat('cart_saved', 'Zapisano %s', updated)) : ''}</p>
          <div class="cart-page__item-actions">
            <a class="btn btn--primary" href="${escapeHtml(resumeDraftUrl(item))}">${escapeHtml(i18n('cart_continue', 'Kontynuuj'))}</a>
            <button type="button" class="cart-page__item-remove js-cart-remove" data-draft-id="${escapeHtml(item.id)}">${escapeHtml(i18n('cart_remove', 'Usuń'))}</button>
          </div>
        </div>
      </div>
    `;
    list.appendChild(li);
  });
};

const readCheckoutContact = (root, fallback) => {
  if (fallback && contactComplete(fallback)) {
    return {
      ...fallback,
      terms: true,
      rodo: true,
    };
  }
  const fields = root.querySelector('.js-cart-checkout-fields');
  if (!fields) return null;
  const contact = {
    first_name: fields.querySelector('[name="cart-first-name"]')?.value?.trim() || '',
    last_name: fields.querySelector('[name="cart-last-name"]')?.value?.trim() || '',
    email: fields.querySelector('[name="cart-email"]')?.value?.trim() || '',
    phone: fields.querySelector('[name="cart-phone"]')?.value?.trim() || '',
    phone_prefix: '+48',
    terms: Boolean(fields.querySelector('[name="cart-terms"]')?.checked),
    rodo: Boolean(fields.querySelector('[name="cart-terms"]')?.checked),
  };
  if (!contactComplete(contact) || !contact.terms) return null;
  return contact;
};

const initCartCheckout = (root) => {
  const submitBtn = root.querySelector('.js-cart-checkout-submit');
  const hint = root.querySelector('.js-cart-checkout-hint');
  const turnstileHost = root.querySelector('.js-cart-turnstile');
  const successModal = root.querySelector('.js-cart-success');
  const successText = root.querySelector('.js-cart-success-text');
  const successOk = root.querySelector('.js-cart-success-ok');
  if (!submitBtn) return;

  let lastCartLeadSignature = '';
  let cartLeadTimer = 0;

  const sessionSubmissionId = (ready) => {
    if (!isSubmissionId(root.dataset.crmLeadId || '')) {
      root.dataset.crmLeadId = reuseSubmissionId(
        ready.map((item) => item.payload?.crm_lead_submission_id),
        newSubmissionId,
      );
    }
    return root.dataset.crmLeadId;
  };

  const readLeadContact = (fallback) => {
    const fields = root.querySelector('.js-cart-checkout-fields');
    const fromFields = {
      first_name: fields?.querySelector('[name="cart-first-name"]')?.value?.trim() || '',
      last_name: fields?.querySelector('[name="cart-last-name"]')?.value?.trim() || '',
      email: fields?.querySelector('[name="cart-email"]')?.value?.trim() || '',
      phone: fields?.querySelector('[name="cart-phone"]')?.value?.trim() || '',
    };
    if (isLeadContactValid(fromFields)) return fromFields;
    if (fallback && isLeadContactValid(fallback)) {
      return {
        first_name: String(fallback.first_name || '').trim(),
        last_name: String(fallback.last_name || '').trim(),
        email: String(fallback.email || '').trim(),
        phone: String(fallback.phone || '').trim(),
      };
    }
    return null;
  };

  const sendCartLead = (ready, contact) => {
    if (!ready.length || !contact || !isLeadContactValid(contact)) return;
    const trainingType = trainingTypeForItems(ready.map((item) => item.payload || {}));
    const signature = leadSignature(contact, trainingType);
    if (!shouldPostLead(lastCartLeadSignature, signature)) return;
    lastCartLeadSignature = signature;
    window.clearTimeout(cartLeadTimer);
    const submissionId = sessionSubmissionId(ready);
    const bookingType = ready.every((item) => item.type === ready[0].type)
      ? ready[0].type
      : 'course';
    const payload = {
      type: bookingType,
      submissionId,
      crm_lead_submission_id: submissionId,
      contact,
    };
    if (trainingType) payload.trainingType = trainingType;
    postReservationLead({
      restUrl: window.hskiBooking?.restUrl || '/wp-json/hski/v1/booking',
      restNonce: window.hskiBooking?.restNonce || '',
      payload,
    });
  };

  const scheduleCartLead = () => {
    window.clearTimeout(cartLeadTimer);
    cartLeadTimer = window.setTimeout(() => {
      const ready = collectReadyCartItems();
      sendCartLead(ready, readLeadContact(pickSharedContact(ready)));
    }, 700);
  };

  root.querySelector('.js-cart-checkout-fields')?.addEventListener('input', scheduleCartLead);
  root.querySelector('.js-cart-checkout-fields')?.addEventListener('change', scheduleCartLead);

  const showHint = (message, { success = false } = {}) => {
    if (!hint) return;
    hint.hidden = !message;
    hint.textContent = message || '';
    hint.classList.toggle('is-success', Boolean(success && message));
  };

  const closeSuccessModal = () => {
    if (!successModal) return;
    if (typeof successModal.close === 'function' && successModal.open) successModal.close();
    else successModal.removeAttribute('open');
  };

  const openSuccessModal = (message) => {
    if (successText) {
      successText.textContent = message || i18n('cart_success_email', 'Potwierdzenie znajdziesz w skrzynce e-mail.');
    }
    if (!successModal) {
      showHint(message, { success: true });
      return;
    }
    if (typeof successModal.showModal === 'function') successModal.showModal();
    else successModal.setAttribute('open', 'open');
  };

  successOk?.addEventListener('click', closeSuccessModal);
  successModal?.addEventListener('click', (event) => {
    if (event.target === successModal) closeSuccessModal();
  });
  successModal?.addEventListener('cancel', (event) => {
    event.preventDefault();
    closeSuccessModal();
  });

  const ensureTurnstile = async () => {
    const cfg = getTurnstileConfig();
    if (!cfg.enabled || !turnstileHost) return null;
    turnstileHost.hidden = false;
    if (!turnstileWidget) {
      turnstileWidget = await mountTurnstile(turnstileHost);
    }
    return turnstileWidget;
  };

  submitBtn.addEventListener('click', async () => {
    const ready = collectReadyCartItems();
    if (!ready.length) {
      showHint(i18n('cart_finish_students', 'Dokończ dane kursantów w rezerwacjach kursu lub lekcji, potem wróć tutaj.'));
      return;
    }

    const shared = pickSharedContact(ready);
    const contact = readCheckoutContact(root, shared);
    if (!contact) {
      showHint(i18n('cart_need_contact_terms', 'Uzupełnij dane zamawiającego i zaakceptuj regulamin.'));
      syncCheckoutPanel(root);
      return;
    }

    showHint('');
    submitBtn.disabled = true;
    const original = submitBtn.textContent;
    submitBtn.textContent = i18n('cart_sending', 'Wysyłanie…');

    try {
      const widget = await ensureTurnstile();
      const cfg = getTurnstileConfig();
      const submissionId = sessionSubmissionId(ready);
      sendCartLead(ready, contact);
      const body = {
        submissionId,
        contact,
        items: ready.map((item) => ({
          type: item.type,
          draft_id: item.id,
          offer_id: item.payload?.offer_id || item.payload?.details?.offer_id || '',
          details: item.payload?.details || {},
        })),
      };
      if (cfg.enabled) {
        const token = widget?.getToken?.() || '';
        if (!token) {
          widget?.reveal?.({ scroll: true });
          throw new Error(i18n('cart_turnstile', 'Potwierdź, że nie jesteś botem — zaznacz weryfikację poniżej.'));
        }
        body['cf-turnstile-response'] = token;
      }

      const restUrl = `${String(window.hskiBooking?.restUrl || '/wp-json/hski/v1/booking').replace(/\/$/, '')}/cart`;
      const restNonce = window.hskiBooking?.restNonce || '';
      const response = await fetch(restUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(restNonce ? { 'X-WP-Nonce': restNonce } : {}),
        },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || i18n('cart_send_failed', 'Nie udało się wysłać zamówienia.'));
      }

      ready.forEach((item) => removeDraft(item.id));
      renderCart(root);
      openSuccessModal(
        data.reference
          ? i18nFormat('cart_sent_ref', 'Zamówienie wysłane. Numer: %s', data.reference)
          : i18n('cart_sent_email', 'Zamówienie wysłane. Potwierdzenie znajdziesz w skrzynce e-mail.'),
      );
    } catch (error) {
      turnstileWidget?.reset?.();
      showHint(error.message || i18n('cart_send_error', 'Wystąpił błąd podczas wysyłania.'));
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = original;
      syncCheckoutPanel(root);
    }
  });
};

const initCartConfirm = (root) => {
  const modal = root.querySelector('.js-cart-confirm');
  const textEl = root.querySelector('.js-cart-confirm-text');
  const okBtn = root.querySelector('.js-cart-confirm-ok');
  if (!modal) return;

  let pendingId = '';
  let removing = false;

  const closeConfirm = () => {
    pendingId = '';
    if (typeof modal.close === 'function' && modal.open) modal.close();
    else modal.removeAttribute('open');
  };

  const openConfirm = (id, name) => {
    pendingId = id;
    if (textEl) {
      textEl.textContent = name
        ? i18nFormat('cart_remove_named', 'Usuniesz „%s” z koszyka. Zapisany postęp zniknie.', name)
        : i18n('cart_remove_generic', 'Ta niedokończona rezerwacja zniknie z koszyka. Zapisanego postępu nie da się odzyskać.');
    }
    if (typeof modal.showModal === 'function') modal.showModal();
    else modal.setAttribute('open', 'open');
  };

  root.addEventListener('click', (event) => {
    const btn = event.target.closest('.js-cart-remove');
    if (!btn || removing) return;
    const id = btn.dataset.draftId;
    if (!id) return;
    const name = btn.closest('.cart-page__item')
      ?.querySelector('.cart-page__item-title')
      ?.textContent
      ?.trim() || '';
    openConfirm(id, name);
  });

  okBtn?.addEventListener('click', async () => {
    if (!pendingId || removing) return;
    const id = pendingId;
    removing = true;
    okBtn.disabled = true;
    closeConfirm();

    try {
      await removeCartItem(root, id);
    } finally {
      removing = false;
      okBtn.disabled = false;
    }
  });

  root.querySelectorAll('.js-cart-confirm-cancel').forEach((btn) => {
    btn.addEventListener('click', closeConfirm);
  });

  modal.addEventListener('click', (event) => {
    if (event.target === modal) closeConfirm();
  });

  modal.addEventListener('cancel', (event) => {
    event.preventDefault();
    closeConfirm();
  });
};

const initCartPage = () => {
  const root = document.querySelector('.js-cart-page');
  if (!root) {
    initCartUi();
    return;
  }

  const refresh = () => renderCart(root);
  refresh();
  initCartUi();
  initCartConfirm(root);
  initCartCheckout(root);

  window.addEventListener(CART_EVENT, () => {
    if (suppressCartRefresh || root.querySelector('.cart-page__item.is-removing')) return;
    renderCart(root);
  });
};

export default initCartPage;
